<template>
  <div class="form-control w-full config-list">
    <ListSelect
      :modelValue="currentLabel"
      @update:modelValue="(c: ConfigOption) => selectConfig(c)"
      :options="configurations"
      :clearable="false"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from "vue";
import { useToast } from "vue-toastification";
import { useBackendStore, useConfigFiltersStore } from "@/stores";
import { ConfigShort } from "@/stores/backend";
import ListSelect from "@/components/utils/ListSelect.vue";

const toast = useToast();
const backend = useBackendStore();
const filters = useConfigFiltersStore();

interface ConfigOption {
  id: number;
  label: string;
}

const label = (c: ConfigShort): string =>
  c.owner != null ? `${c.name} @${c.owner_name}` : c.name;

const currentLabel = computed(() => {
  if (backend.currentConfig == null) return "__default__";
  const current = backend.configList.find(
    (c) => c.id == backend.currentConfig?.id
  );
  if (current == undefined) return backend.currentConfig.name;
  return current.name;
});

interface ConfigShortSort {
  id: number;
  name: string;
}

const configSortKey = (a: ConfigShortSort, b: ConfigShortSort) => {
  // Prioritize default config as the first one (by name, as ID may not be the lowest one)
  if (a.name !== b.name) {
    if (a.name === "DEFAULT") return -1;
    if (b.name === "DEFAULT") return Infinity;
  }
  // Then sort by ID
  return a.id - b.id;
};

const configurations = computed(() => {
  return backend.configList
    .map((c) => ({
      id: c.id,
      owner: c.owner,
      label: label(c),
      name: c.name,
    }))
    .filter(
      (c) => filters.owned == false || c.owner == backend.user?.id?.toString()
    )
    .sort(configSortKey);
});

const selectConfig = (c: ConfigOption) => {
  backend.setCurrentConfig(c.id);
  //.then(() => (currentLabel.value = c.label));
};

onMounted(() =>
  backend.fetchConfigList().then((configs) => {
    // Select default config
    if (configs.length == 0) {
      toast.error("Could not retrieve any configurations from server");
    } else {
      const defaultConfig = configs.sort(configSortKey)[0];
      selectConfig({ id: defaultConfig.id, label: label(defaultConfig) });
    }
  })
);
</script>

<style>
.config-list .v-select .vs__selected-options {
  @apply flex-nowrap;
}
</style>
